import { useId, type ReactNode } from 'react';
import { Box, ButtonBase, Chip, Collapse, Typography } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface HelpSectionCardProps {
  title: string;
  /** How many entries the card holds, shown beside its title. */
  count: number;
  /** The count as a screen reader reads it ("3 fields"). */
  countLabel: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}

/**
 * A collapsible group in the help panel — a form section's fields, a module's pages — with its number of entries.
 * Its row looks as "Show field information" does (HelpViews): a thin border, the text in body2, the chevron on the right.
 */
export function HelpSectionCard({ title, count, countLabel, expanded, onToggle, children }: HelpSectionCardProps) {
  const contentId = useId();
  return (
    <Box component="section" sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      <Typography component="h3" variant="body2" sx={{ m: 0 }}>
        <ButtonBase
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={contentId}
          sx={{ width: '100%', justifyContent: 'flex-start', gap: 1, px: 2, py: 1, font: 'inherit', textAlign: 'left', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Box component="span" sx={{ flex: 1 }}>{title}</Box>
          <Chip size="small" label={count} aria-label={countLabel} sx={{ height: 20, fontWeight: 500 }} />
          {expanded ? <ExpandMore fontSize="small" color="action" /> : <ChevronRight fontSize="small" color="action" />}
        </ButtonBase>
      </Typography>
      <Collapse in={expanded} unmountOnExit>
        <Box id={contentId} sx={{ borderTop: 1, borderColor: 'divider' }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
